import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { centsToPtbr, ptbrToCents } from '@/lib/formatters';
import { type ProductFormData, productSchema } from '@/schemas/commercial/product.schema';
import type { IProduct, IProductCategory } from '@/types/commercial/product';

interface SpecEntry {
  key: string;
  value: string;
}

interface UseProductFormProps {
  product?: IProduct | null;
  defaultCategoryId?: string;
}

export function useProductForm({ product, defaultCategoryId }: UseProductFormProps = {}) {
  const [specEntries, setSpecEntries] = useState<SpecEntry[]>([{ key: '', value: '' }]);
  const [fileEntries, setFileEntries] = useState<string[]>(['']);

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: '',
      category_id: defaultCategoryId || '',
      color: '#000000',
      price: '0,00',
      description: '',
      spec: {},
      files: [],
      active: true,
    },
  });

  useEffect(() => {
    if (product) {
      form.reset({
        name: product.name,
        category_id: product.category_id,
        color: product.color,
        price: centsToPtbr(product.priceInCents),
        description: product.description || '',
        spec: product.spec || {},
        files: product.files || [],
        active: product.active,
      });

      // Populate spec entries
      const specs = product.spec
        ? Object.entries(product.spec).map(([key, value]) => ({ key, value }))
        : [{ key: '', value: '' }];
      setSpecEntries(specs.length > 0 ? specs : [{ key: '', value: '' }]);

      // Populate file entries
      const files = product.files || [];
      setFileEntries(files.length > 0 ? files : ['']);
    }
  }, [product, form]);

  const handleCategoryChange = (category: IProductCategory) => {
    form.setValue('category_id', category.id);
  };

  const addSpecEntry = () => {
    setSpecEntries([...specEntries, { key: '', value: '' }]);
  };

  const removeSpecEntry = (index: number) => {
    if (specEntries.length > 1) {
      setSpecEntries(specEntries.filter((_, i) => i !== index));
    }
  };

  const updateSpecEntry = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...specEntries];
    updated[index][field] = value;
    setSpecEntries(updated);
  };

  const addFileEntry = () => {
    setFileEntries([...fileEntries, '']);
  };

  const removeFileEntry = (index: number) => {
    if (fileEntries.length > 1) {
      setFileEntries(fileEntries.filter((_, i) => i !== index));
    }
  };

  const updateFileEntry = (index: number, value: string) => {
    const updated = [...fileEntries];
    updated[index] = value;
    setFileEntries(updated);
  };

  const getFormData = (): ProductFormData & { priceInCents: number } => {
    const data = form.getValues();

    // Process spec entries into object
    const spec = specEntries
      .filter((entry) => entry.key.trim() && entry.value.trim())
      .reduce(
        (acc, entry) => {
          acc[entry.key.trim()] = entry.value.trim();
          return acc;
        },
        {} as Record<string, string>,
      );

    // Process file entries
    const files = fileEntries.filter((file) => file.trim());

    return {
      ...data,
      priceInCents: ptbrToCents(data.price),
      spec: Object.keys(spec).length > 0 ? spec : undefined,
      files: files.length > 0 ? files : undefined,
    };
  };

  const resetForm = () => {
    form.reset({
      name: '',
      category_id: '',
      color: '#000000',
      price: '0,00',
      description: '',
      spec: {},
      files: [],
      active: true,
    });
    setSpecEntries([{ key: '', value: '' }]);
    setFileEntries(['']);
  };

  return {
    form,
    specEntries,
    fileEntries,
    addSpecEntry,
    removeSpecEntry,
    updateSpecEntry,
    addFileEntry,
    removeFileEntry,
    updateFileEntry,
    handleCategoryChange,
    getFormData,
    resetForm,
  };
}
